import { Component, HostListener, inject, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { ExamTabLockService } from './core/security/exam-tab-lock.service';
import { DesktopRequiredComponent } from './shared/components/desktop-required/desktop-required';
import { DuplicateTabOverlayComponent } from './shared/components/duplicate-tab-overlay/duplicate-tab-overlay';
import { SecurityLockOverlayComponent } from './shared/components/security-lock-overlay/security-lock-overlay';

const MIN_DESKTOP_WIDTH = 1024;
const MIN_TABLET_WIDTH = 600;
/** Touch screens narrower than this in portrait are phones (CSS px). */
const MAX_MOBILE_WIDTH = 580;

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, DesktopRequiredComponent, SecurityLockOverlayComponent, DuplicateTabOverlayComponent],
  templateUrl: './app.html',
})
export class App {
  // Created eagerly so every tab answers the other tabs' duplicate-tab probes.
  private readonly tabLock = inject(ExamTabLockService);
  protected readonly title = signal('mock-test');
  protected readonly isMobileViewport = signal(this.shouldBlockViewport());
  protected readonly isTabletDevice = signal(this.detectTablet());
  protected readonly isSmallScreen = signal(this.detectSmallScreen());
  /**
   * While an exam runs, the blocker is shown over the question paper instead
   * of replacing it, so turning a tablet to portrait doesn't destroy the
   * attempt (and end its event session) — the student only has to rotate back.
   */
  protected readonly isExamRunning = this.tabLock.isExamRunningHere;

  @HostListener('window:resize')
  protected onWindowResize(): void {
    this.isMobileViewport.set(this.shouldBlockViewport());
    this.isTabletDevice.set(this.detectTablet());
    this.isSmallScreen.set(this.detectSmallScreen());
  }

  @HostListener('window:orientationchange')
  protected onOrientationChange(): void {
    // Orientation change fires before resize; defer to let dimensions settle.
    setTimeout(() => {
      this.isMobileViewport.set(this.shouldBlockViewport());
      this.isTabletDevice.set(this.detectTablet());
      this.isSmallScreen.set(this.detectSmallScreen());
    }, 150);
  }

  private shouldBlockViewport(): boolean {
    // Tablets must be in landscape whatever their width — an iPad Pro is
    // 1024px wide in portrait, so the width check below would let it through.
    if (this.detectTablet() && this.isScreenPortrait()) {
      return true;
    }

    // Desktop-sized screens are always allowed.
    if (window.innerWidth >= MIN_DESKTOP_WIDTH) {
      return false;
    }

    // Tablets in landscape are allowed even if width < 1024.
    if (this.detectTablet() && this.isLandscape()) {
      return false;
    }

    return true;
  }

  private detectTablet(): boolean {
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const screenWidth = Math.max(screen.width, screen.height);
    return hasTouchScreen && screenWidth >= MIN_TABLET_WIDTH && !this.detectSmallScreen();
  }

  /** Phones: blocked in both orientations, so measure the portrait width (the short side). */
  private detectSmallScreen(): boolean {
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    return hasTouchScreen && Math.min(screen.width, screen.height) < MAX_MOBILE_WIDTH;
  }

  private isLandscape(): boolean {
    return window.innerWidth > window.innerHeight;
  }

  /**
   * The device's orientation, not the window's — a touchscreen laptop with a
   * narrow browser window is still a landscape screen. Older iOS Safari has no
   * screen.orientation (and never swaps screen.width/height), so fall back to
   * the viewport media query there.
   */
  private isScreenPortrait(): boolean {
    const type = screen.orientation?.type;
    return type ? type.startsWith('portrait') : window.matchMedia('(orientation: portrait)').matches;
  }
}
